<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold pa-0"
        >Discover Pages ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>

      <v-row v-show="showSearch" align="center" style="max-width: 300px">
        <v-text-field
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search title, subtitle, introduction or content"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn icon class="ml-5" @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              color="#383d3f"
              class="mr-1"
              icon
              @click="exportList"
              v-on="on"
            >
              <v-icon>{{ icons.export }}</v-icon>
            </v-btn>
          </template>
          <span>Export to Excel</span>
        </v-tooltip>
      </template>
    </v-app-bar>

    <div class="d-flex mt-6">
      <div>
        <v-btn
          color="primary"
          @click="
            $router.push({
              name: 'discoveryPage.new',
            })
          "
        >
          <v-icon class="mr-1" small>{{ icons.add }}</v-icon
          >Add new discover page
        </v-btn>
      </div>

      <v-spacer></v-spacer>

      <div class="d-flex">
        <v-select
          class="ml-10"
          hide-details
          flat
          solo
          dense
          item-text="label"
          item-value="value"
          v-model="defaultFilter"
          :items="filterBy"
          style="max-width: 200px"
          @change="filterDiscoveryPage"
        />

        <v-btn
          icon
          class="ml-3"
          :color="sortOrder ? 'primary' : ''"
          @click="
            sortOrder = true
            filterDiscoveryPage()
          "
        >
          <v-icon>{{ icons.arrowUp }}</v-icon>
        </v-btn>

        <v-btn
          icon
          :color="!sortOrder ? 'primary' : ''"
          @click="
            sortOrder = false
            filterDiscoveryPage()
          "
        >
          <v-icon>{{ icons.arrowDown }}</v-icon>
        </v-btn>
      </div>
    </div>

    <div
      class="mx-4 pb-10"
      id="discovery-page-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
      min-width="200px"
    >
      <v-card
        v-for="discoveryPage in discoveryPages"
        :key="discoveryPage.id"
        :loading="loading"
        class="mx-auto my-12"
        max-width="500"
      >
        <template slot="progress">
          <v-progress-linear
            color="deep-purple"
            height="10"
            indeterminate
          ></v-progress-linear>
        </template>

        <v-img
          height="250"
          :src="discoveryPage.cover_photo ? discoveryPage.cover_photo.url : ''"
          class="white--text align-end"
          :style="discoveryPage.cover_photo ? '' : 'background: grey'"
          gradient="to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.8)"
          :lazy-src="
            discoveryPage.cover_photo ? discoveryPage.cover_photo.url : ''
          "
        >
          <v-icon
            small
            color="white"
            class="card__edit"
            @click="
              $router.push({
                name: 'discoveryPage.details',
                params: { id: discoveryPage.id },
              })
            "
          >
            {{ icons.edit }}
          </v-icon>

          <v-chip
            label
            small
            class="card__category white--text"
            style="
              background: linear-gradient(
                302.47deg,
                #f55672 -1.18%,
                #ffa890 97.85%
              );
            "
          >
            {{ discoveryPage.discovery.discovery_name }}
          </v-chip>

          <v-card-text style="margin-bottom: -24px">
            <div class="caption">Added on {{ discoveryPage.addedDate }}</div>
          </v-card-text>

          <v-card-title>
            <div class="card__title">
              {{ discoveryPage.title }}
            </div>

            <v-icon
              class="card__premium text-left"
              color="white"
              v-if="discoveryPage.is_premium"
            >
              {{ icons.premium }}
            </v-icon>
          </v-card-title>

          <v-card-text>
            <div class="card__subtitle">{{ discoveryPage.subtitle }}</div>
          </v-card-text>
        </v-img>

        <v-card-text>
          <v-chip-group column>
            <v-chip
              small
              color="primary"
              class="card__tags primary--text px-3 py-4"
              outlined
              style="background-color: #fdf2ee !important"
              v-for="tag in discoveryPage.tags"
              :key="tag.id"
            >
              {{ tag.tag_name }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
      </v-card>

      <div
        class="d-flex justify-center mt-3"
        v-if="!(discoveryPages.length > 0) && !loading"
      >
        <p>No match found.</p>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" right>
      {{ snackbar.message }}
      <v-btn color="white" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiArrowUp,
  mdiArrowDown,
  mdiPlus,
  mdiPencil,
  mdiLock,
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'

export default {
  name: 'DiscoveryPagesPage',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll,
  },

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      filter: {
        search: '',
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        arrowUp: mdiArrowUp,
        arrowDown: mdiArrowDown,
        add: mdiPlus,
        edit: mdiPencil,
        premium: mdiLock,
      },
      snackbar: {
        show: false,
        message: null,
        color: null,
      },
      defaultFilter: 'title',
      sortOrder: true,
      filterBy: [
        {
          value: 'title',
          label: 'Title',
        },
        {
          value: 'subtitle',
          label: 'Subtitle',
        },
        {
          value: 'is_premium',
          label: 'Is Premium?',
        },
        {
          value: 'created_at',
          label: 'Added date',
        },
      ],
    }
  },

  created() {
    this.clearDiscoveryPages()
    this.fetchDiscoveryPages()
  },

  computed: {
    ...mapState({
      discoveryPages: (state) => state.discoveryPage.list,
      listMeta: (state) => state.discoveryPage.listMeta,
    }),

    paginationLength() {
      return (this.discoveryPages.total / this.listMeta.per_page) | 0
    },
  },

  methods: {
    ...mapActions({
      getDiscoveryPages: 'discoveryPage/getDiscoveryPages',
      exportToExcel: 'discoveryPage/exportToExcel',
    }),

    ...mapMutations({
      clearDiscoveryPages: 'discoveryPage/clearDiscoveryPageList',
    }),

    pageChanged(page) {
      this.fetchDiscoveryPages(page)
    },

    filterDiscoveryPage(filterValue) {
      this.clearDiscoveryPages()
      this.fetchDiscoveryPages(null, filterValue)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchDiscoveryPages(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function () {
      this.clearDiscoveryPages()
      this.fetchDiscoveryPages()
    }, 600),

    async fetchDiscoveryPages(page = 1) {
      if (this.loading) return
      let params = {
        page,
        sort: this.sortOrder
          ? `${this.defaultFilter}`
          : `-${this.defaultFilter}`,
      }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      this.loading = true
      await this.getDiscoveryPages(params)
      this.loading = false
    },

    async exportList() {
      this.loading = true
      await this.exportToExcel({
        search: this.filter.search,
      })
        .then((response) => {
          const url = URL.createObjectURL(
            new Blob([response.data], {
              type: 'application/vnd.ms-excel',
            })
          )
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'discovery_pages_list.xlsx')
          document.body.appendChild(link)
          link.click()
          this.showSnackbar('File successfully downloaded', 'success')
        })
        .catch(() => {
          this.showSnackbar('Ops! Something went wrong', 'red')
        })
      this.loading = false
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },
    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },

    formatText(str) {
      return str.replace(/<\/?[^>]+(>|$)/g, '')
    },
  },
}
</script>

<style scoped>
.card__category {
  position: absolute;
  top: 12px;
  left: 12px;
  font-family: Outfit, Outfit-Regular;
  font-size: 14px;
}
.card__edit {
  position: absolute;
  top: 12px;
  right: 12px;
}
.card__premium {
  position: absolute;
  right: 12px;
}
.card__title {
  font-family: Outfit, Outfit-Regular;
  font-size: 28px;
  font-weight: bold;
}
.card__subtitle {
  font-family: Outfit, Outfit-Regular;
  font-size: 16px;
  margin-top: -14px;
}
.card__body {
  font-size: 14px;
}
.card__content {
  background: #fdf2ee;
  border-radius: 10px;
}
.card__tags {
  border-radius: 10px;
  text-transform: lowercase;
}
</style>
